import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LogOut, Loader2 } from 'lucide-react';

import useMyOffboardingApi from './config';
import Tabs from '../../components/ui/Tabs';
import OverviewTab from './tab-pages/OverviewTab';
import TasksTab from './tab-pages/TasksTab';
import HandoverTab from './tab-pages/HandoverTab';
import ExitInterviewTab from './tab-pages/ExitInterviewTab';

const Page = styled.div`
  padding: 8px 4px 40px;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin: 0;
`;

const spin = keyframes`to { transform: rotate(360deg); }`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  text-align: center;
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.9s linear infinite;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const EmptyIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const EmptyTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyText = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  max-width: 420px;
`;

export default function MyOffboardingPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  const {
    loading, home, tasks, handover, interview,
    startTask, completeTask, saveHandover, submitHandover, acknowledgeHandover, submitInterview,
  } = useMyOffboardingApi();

  const tabs = [
    { key: 'overview', label: t('myOffboarding.tab.overview') },
    { key: 'tasks', label: t('myOffboarding.tab.tasks') },
    { key: 'handover', label: t('myOffboarding.tab.handover') },
    { key: 'interview', label: t('myOffboarding.tab.interview') },
  ];

  return (
    <Page>
      <Header>
        <Title>{t('myOffboarding.title')}</Title>
        <Subtitle>{t('myOffboarding.subtitle')}</Subtitle>
      </Header>

      {loading && (
        <Centered>
          <Spinner size={32} />
        </Centered>
      )}

      {!loading && !home && (
        <Centered>
          <EmptyIconWrap>
            <LogOut size={28} />
          </EmptyIconWrap>
          <EmptyTitle>{t('myOffboarding.empty.title')}</EmptyTitle>
          <EmptyText>{t('myOffboarding.empty.text')}</EmptyText>
        </Centered>
      )}

      {!loading && home && (
        <>
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

          {activeTab === 'overview' && (
            <OverviewTab home={home} onGoTab={setActiveTab} />
          )}
          {activeTab === 'tasks' && (
            <TasksTab tasks={tasks} onStart={startTask} onComplete={completeTask} />
          )}
          {activeTab === 'handover' && (
            <HandoverTab
              home={home}
              handover={handover}
              onSaveDraft={saveHandover}
              onSubmit={submitHandover}
              onAcknowledge={acknowledgeHandover}
            />
          )}
          {activeTab === 'interview' && (
            <ExitInterviewTab interview={interview} onSubmit={submitInterview} />
          )}
        </>
      )}
    </Page>
  );
}
